import { useState, type ReactNode } from 'react'
import { Alert, Button, Card, Flex, Space, Typography, Upload, theme } from 'antd'
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { ThermalLogo } from '@/shared/ui/ThermalLogo'
import { RECEIPT_FONT } from '@/features/cashdesk/receiptPrint'
import { LOGO_ACCEPT, MIN_LOGO_WIDTH_PX, removeLogo, uploadLogo, type TenantBranding } from './brandingApi'

interface Props {
  branding: TenantBranding | undefined
  loading: boolean
  editable: boolean
}

const PREVIEW_HEIGHT = 96
const DARK_SURFACE = '#1f1f1f'

export function BrandingLogoCard({ branding, loading, editable }: Props) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const onSaved = (next: TenantBranding) => queryClient.setQueryData(queryKeys.tenantBranding, next)
  const upload = useMutation({ mutationFn: uploadLogo, onSuccess: onSaved })
  const remove = useMutation({ mutationFn: removeLogo, onSuccess: onSaved })
  const failed = upload.error ?? remove.error
  const busy = upload.isPending || remove.isPending
  const logoUrl = branding?.logoUrl ?? null
  const companyName = branding?.companyName ?? user?.tenant.name ?? null

  return (
    <Card title={t('settings.branding.logo')} loading={loading}>
      <Flex vertical gap={16}>
        {failed ? <Alert type="error" showIcon message={translateError(failed, t)} /> : null}

        {logoUrl ? (
          <LogoPreviews key={logoUrl} logoUrl={logoUrl} companyName={companyName} />
        ) : (
          <Flex
            align="center"
            justify="center"
            style={{
              height: PREVIEW_HEIGHT,
              border: `1px dashed ${token.colorBorder}`,
              borderRadius: token.borderRadius,
            }}
          >
            <Typography.Text type="secondary">{t('settings.branding.noLogo')}</Typography.Text>
          </Flex>
        )}

        {editable ? (
          <Space wrap>
            <Upload
              accept={LOGO_ACCEPT}
              showUploadList={false}
              disabled={busy}
              beforeUpload={(file) => {
                upload.mutate(file)
                return false
              }}
            >
              <Button icon={<UploadOutlined />} loading={upload.isPending}>
                {logoUrl ? t('settings.branding.replaceLogo') : t('settings.branding.uploadLogo')}
              </Button>
            </Upload>
            {logoUrl ? (
              <Button danger icon={<DeleteOutlined />} loading={remove.isPending} disabled={busy} onClick={() => remove.mutate()}>
                {t('settings.branding.removeLogo')}
              </Button>
            ) : null}
          </Space>
        ) : null}

        <ul style={{ margin: 0, paddingInlineStart: 20, color: token.colorTextSecondary }}>
          <li>{t('settings.branding.tipFormat')}</li>
          <li>{t('settings.branding.tipSize', { min: MIN_LOGO_WIDTH_PX })}</li>
          <li>{t('settings.branding.tipThermal')}</li>
        </ul>
      </Flex>
    </Card>
  )
}

function LogoPreviews({ logoUrl, companyName }: { logoUrl: string; companyName: string | null }) {
  const { t } = useTranslation()
  const [naturalWidth, setNaturalWidth] = useState<number | null>(null)
  const logoStyle = { maxHeight: 56, maxWidth: '80%', objectFit: 'contain' } as const

  return (
    <Flex vertical gap={12}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
        <PreviewTile label={t('settings.branding.previewLight')} background="#ffffff">
          <img
            src={logoUrl}
            alt={t('settings.branding.logo')}
            onLoad={(event) => setNaturalWidth(event.currentTarget.naturalWidth)}
            style={logoStyle}
          />
        </PreviewTile>
        <PreviewTile label={t('settings.branding.previewDark')} background={DARK_SURFACE}>
          <img src={logoUrl} alt="" style={logoStyle} />
        </PreviewTile>
        <PreviewTile label={t('settings.branding.previewThermal')} background="#ffffff">
          <Flex vertical align="center" gap={4} style={{ width: '100%', fontFamily: RECEIPT_FONT, color: '#000' }}>
            <ThermalLogo src={logoUrl} style={{ ...logoStyle, maxHeight: 44 }} />
            {companyName ? (
              <span dir="auto" style={{ fontSize: 11, fontWeight: 700, maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {companyName}
              </span>
            ) : null}
          </Flex>
        </PreviewTile>
      </div>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('settings.branding.thermalNote')}
      </Typography.Text>

      {naturalWidth !== null && naturalWidth < MIN_LOGO_WIDTH_PX ? (
        <Alert
          type="warning"
          showIcon
          message={t('settings.branding.lowResolution', { width: naturalWidth, min: MIN_LOGO_WIDTH_PX })}
        />
      ) : null}
    </Flex>
  )
}

function PreviewTile({ label, background, children }: { label: string; background: string; children: ReactNode }) {
  const { token } = theme.useToken()
  return (
    <figure style={{ margin: 0 }}>
      <Flex
        align="center"
        justify="center"
        style={{
          height: PREVIEW_HEIGHT,
          background,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadius,
          overflow: 'hidden',
        }}
      >
        {children}
      </Flex>
      <figcaption style={{ marginTop: 6, fontSize: 12, color: token.colorTextSecondary, textAlign: 'center' }}>
        {label}
      </figcaption>
    </figure>
  )
}
